import { Alert, Card, CardHeader, LoadingBlock } from "../../components/ui";
import { useTranslation } from "react-i18next";
import { LogViewer } from "../../components/LogViewer";
import { useApp, useAppLogs } from "../../lib/queries";
import { useAppTabContext } from "./context";

/*
 * Runtime logs — the process's own stdout, not the build output that lives on
 * the Deployments tab.
 *
 * This did not exist for a while, and the codebase said so in two places: that
 * only databases had a REST log endpoint and applications did not, on the
 * strength of an upstream issue. That issue is about a general container log
 * endpoint and is still open, but `application.readLogs` is a separate thing
 * and is live, so the gap was ours.
 */
export function LogsTab() {
  const { t } = useTranslation("app");
  const { orgId, appId } = useAppTabContext();
  const { data: app } = useApp(orgId, appId);

  const running = app?.status.value === "running";
  const { data, isPending, error } = useAppLogs(orgId, appId, { live: running });

  return (
    <Card>
      <CardHeader
        title={t("logs.title")}
        description={t("logs.description")}
      />
      <div className="p-5">
        {isPending ? (
          <LoadingBlock label={t("logs.loading")} />
        ) : error ? (
          <Alert>{error.message}</Alert>
        ) : data?.unavailable ? (
          <Alert tone="warning">
            {t("logs.unreachable")}
          </Alert>
        ) : (
          <LogViewer
            logs={data?.logs ?? ""}
            streaming={running}
            downloadName={`${app?.slug ?? "app"}-runtime`}
            emptyMessage={
              running
                ? t("logs.runningEmpty")
                : t("logs.stoppedEmpty")
            }
          />
        )}
      </div>
    </Card>
  );
}
